import { CopyOutlined, DeleteOutlined, LinkOutlined } from "@ant-design/icons"
import { App, Avatar, Button, Checkbox, Empty, Flex, Input, Modal, Select, Skeleton, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import {
  changeAttachmentAccessPermission,
  createAttachmentAccess,
  createAttachmentShareLink,
  disableAttachmentShareLink,
  getAttachmentAccess,
  getAttachmentShareLinks,
  removeAttachmentAccess
} from "../../Project/api/project-attachment-sharing.services.ts"
import { getProjectDocumentReviewers } from "../../Project/api/project-documents.services.ts"
import type {
  AttachmentPermission,
  ProjectAttachmentAccess,
  ProjectAttachmentShareLink
} from "../../Project/types/project-attachment-sharing.types.ts"
import { readServerMessage } from "../helpers/server-message.ts"
import { type FileItem } from "../types/file-item.types.ts"

const { Text, Title } = Typography

interface ShareDialogProps {
  file: FileItem
  onClose: () => void
}

const PERMISSION_VALUES: AttachmentPermission[] = ["viewer", "contributor", "manager"]

export function ShareDialog({ file, onClose }: ShareDialogProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()

  const [access, setAccess] = useState<ProjectAttachmentAccess[] | null>(null)
  const [links, setLinks] = useState<ProjectAttachmentShareLink[] | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [people, setPeople] = useState<{ label: string; value: string }[]>([])

  const [addingUserId, setAddingUserId] = useState<string | null>(null)
  const [addingPermission, setAddingPermission] = useState<AttachmentPermission>("viewer")
  const [adding, setAdding] = useState(false)
  const [busyAccessId, setBusyAccessId] = useState<string | null>(null)

  const [allowDownload, setAllowDownload] = useState(true)
  const [creatingLink, setCreatingLink] = useState(false)
  const [disablingLinkId, setDisablingLinkId] = useState<string | null>(null)

  const permissionOptions = PERMISSION_VALUES.map((value) => ({
    value,
    label: t(`files.sharing.permission.${value}`)
  }))

  const load = useCallback(async () => {
    setAccess(null)
    setLinks(null)
    setLoadFailed(false)
    const [accessResponse, linksResponse, reviewersResponse] = await Promise.all([
      getAttachmentAccess(file.id),
      getAttachmentShareLinks(file.id),
      getProjectDocumentReviewers(file.id)
    ])
    if (!accessResponse.ok || !accessResponse.data || !linksResponse.ok || !linksResponse.data) {
      setLoadFailed(true)
      setAccess([])
      setLinks([])
      return
    }
    setAccess(accessResponse.data.results)
    setLinks(linksResponse.data.results)
    if (reviewersResponse.ok && reviewersResponse.data) {
      setPeople(reviewersResponse.data)
    }
  }, [file.id])

  useEffect(() => {
    void load()
  }, [load])

  const grantedUserIds = new Set((access ?? []).map((entry) => entry.user?.id))
  const addablePeople = people.filter((person) => !grantedUserIds.has(person.value))
  const activeLink = (links ?? []).find((link) => link.is_active) ?? null

  const handleAdd = async () => {
    if (!addingUserId) return
    setAdding(true)
    const response = await createAttachmentAccess({
      attachment: file.id,
      user_id: addingUserId,
      permission: addingPermission
    })
    setAdding(false)
    if (!response.ok || !response.data) {
      message.error(readServerMessage(response.data) ?? t("files.sharing.addFailed"))
      return
    }
    message.success(t("files.sharing.addSucceeded"))
    setAddingUserId(null)
    setAddingPermission("viewer")
    await load()
  }

  const handleChangePermission = async (
    entry: ProjectAttachmentAccess,
    permission: AttachmentPermission
  ) => {
    setBusyAccessId(entry.id)
    const response = await changeAttachmentAccessPermission(entry.id, permission)
    setBusyAccessId(null)
    if (!response.ok) {
      message.error(readServerMessage(response.data) ?? t("files.sharing.permissionFailed"))
      return
    }
    setAccess((prev) =>
      (prev ?? []).map((item) => (item.id === entry.id ? { ...item, permission } : item))
    )
  }

  const handleRemove = async (entry: ProjectAttachmentAccess) => {
    setBusyAccessId(entry.id)
    const response = await removeAttachmentAccess(entry.id)
    setBusyAccessId(null)
    if (!response.ok) {
      message.error(readServerMessage(response.data) ?? t("files.sharing.removeFailed"))
      return
    }
    message.success(t("files.sharing.removeSucceeded"))
    setAccess((prev) => (prev ?? []).filter((item) => item.id !== entry.id))
  }

  const handleCreateLink = async () => {
    setCreatingLink(true)
    const response = await createAttachmentShareLink({
      attachment: file.id,
      allow_download: allowDownload
    })
    setCreatingLink(false)
    if (!response.ok || !response.data) {
      message.error(readServerMessage(response.data) ?? t("files.sharing.linkCreateFailed"))
      return
    }
    const created = response.data.details.data
    setLinks((prev) => [created, ...(prev ?? [])])
  }

  const handleDisableLink = async (link: ProjectAttachmentShareLink) => {
    setDisablingLinkId(link.id)
    const response = await disableAttachmentShareLink(link.id)
    setDisablingLinkId(null)
    if (!response.ok) {
      message.error(readServerMessage(response.data) ?? t("files.sharing.linkDisableFailed"))
      return
    }
    setLinks((prev) =>
      (prev ?? []).map((item) =>
        item.id === link.id ? { ...item, is_active: false, disabled_at: new Date().toISOString() } : item
      )
    )
  }

  const handleCopyLink = async (link: ProjectAttachmentShareLink) => {
    try {
      await navigator.clipboard.writeText(link.url)
      message.success(t("files.sharing.linkCopied"))
    } catch {
      message.error(t("files.sharing.linkCopyFailed"))
    }
  }

  const loading = access === null || links === null

  return (
    <Modal
      open
      title={t("files.sharing.title", { name: file.name })}
      onCancel={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          {t("files.sharing.done")}
        </Button>
      }
      width={520}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : loadFailed ? (
        <Empty description={t("files.sharing.loadFailed")}>
          <Button size="small" onClick={() => void load()}>
            {t("files.activity.retry")}
          </Button>
        </Empty>
      ) : (
        <Flex vertical gap="middle">
          <Flex vertical gap={8}>
            <Title level={5} className="!mb-0">
              {t("files.sharing.peopleWithAccess")}
            </Title>
            {access.length === 0 ? (
              <Text type="secondary">{t("files.sharing.noPeople")}</Text>
            ) : (
              <Flex vertical gap={4}>
                {access.map((entry) => (
                  <Flex key={entry.id} align="center" justify="space-between" gap={8}>
                    <Flex align="center" gap={8} style={{ minWidth: 0 }}>
                      <Avatar size="small" src={entry.user?.avatar}>
                        {getEntityName(entry.user, "?").charAt(0)}
                      </Avatar>
                      <Text ellipsis={{ tooltip: getEntityName(entry.user, entry.user?.email) }}>
                        {getEntityName(entry.user, entry.user?.email)}
                      </Text>
                    </Flex>
                    <Flex align="center" gap={4}>
                      <Select<AttachmentPermission>
                        size="small"
                        value={entry.permission}
                        options={permissionOptions}
                        disabled={busyAccessId === entry.id}
                        onChange={(value) => void handleChangePermission(entry, value)}
                        style={{ width: 120 }}
                      />
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        disabled={busyAccessId === entry.id}
                        aria-label={t("files.sharing.removeAccess")}
                        onClick={() => void handleRemove(entry)}
                      />
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            )}
          </Flex>

          <Flex vertical gap={8}>
            <Text type="secondary">{t("files.sharing.addPeople")}</Text>
            <Flex gap={8}>
              <Select<string>
                showSearch
                allowClear
                placeholder={t("files.sharing.addPeoplePlaceholder")}
                value={addingUserId ?? undefined}
                onChange={(value) => setAddingUserId(value ?? null)}
                options={addablePeople}
                optionFilterProp="label"
                style={{ flex: 1 }}
              />
              <Select<AttachmentPermission>
                value={addingPermission}
                options={permissionOptions}
                onChange={setAddingPermission}
                style={{ width: 120 }}
              />
              <Button
                type="primary"
                disabled={!addingUserId}
                loading={adding}
                onClick={() => void handleAdd()}
              >
                {t("files.sharing.add")}
              </Button>
            </Flex>
          </Flex>

          <Flex vertical gap={8}>
            <Title level={5} className="!mb-0">
              {t("files.sharing.shareLink")}
            </Title>
            {activeLink ? (
              <Flex vertical gap={8}>
                <Flex gap={8}>
                  <Input readOnly value={activeLink.url} />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => void handleCopyLink(activeLink)}
                  >
                    {t("files.sharing.copyLink")}
                  </Button>
                </Flex>
                <Flex align="center" justify="space-between">
                  <Text type="secondary" className="text-xs">
                    {activeLink.allow_download
                      ? t("files.sharing.downloadAllowed")
                      : t("files.sharing.downloadDisabled")}
                  </Text>
                  <Button
                    danger
                    size="small"
                    loading={disablingLinkId === activeLink.id}
                    onClick={() => void handleDisableLink(activeLink)}
                  >
                    {t("files.sharing.disableLink")}
                  </Button>
                </Flex>
              </Flex>
            ) : (
              <Flex vertical gap={8} align="start">
                <Checkbox
                  checked={allowDownload}
                  onChange={(e) => setAllowDownload(e.target.checked)}
                >
                  {t("files.sharing.allowDownload")}
                </Checkbox>
                <Button
                  icon={<LinkOutlined />}
                  loading={creatingLink}
                  onClick={() => void handleCreateLink()}
                >
                  {t("files.sharing.createLink")}
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>
      )}
    </Modal>
  )
}
